import React, { useCallback, useEffect, useState } from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { AdChoices } from '@bloomberg/media-ui-icons';

import { DropdownMenuDesktop } from '../DropdownMenuDesktop';
import { Logo } from '../Logo';
import { NavProvider } from '../Nav/NavContext';
import { REGIONS, SubmenuItem, THEMES } from '../types';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './Footer.module.css';

export type FooterProps = GlobalProps & {
  name?: string;
  desktopBreakpointClassName?: string;
  prefix?: string;
  cmpText?: string;
  addPrivacyManager: (footerLink: HTMLButtonElement) => unknown;
  region?: REGIONS;
  isWeekend?: boolean;
  locale?: string;
  theme?: THEMES;
  footerMenuItems?: Record<string, SubmenuItem>;
  footerMenuItemsUrl?: string;
  showLogo?: boolean;
  trackingFunction?: (eventAction: string, eventLabel: string) => void;
};

const LOCALE_URL_MAP: Record<string, Record<string, string>> = {
  en: {
    adChoices: '/privacy#advertisements',
    advertise: 'https://www.bloombergmedia.com/contact',
    careers: '/careers/?utm_source=dotcom&utm_medium=footer',
    tos: '/tos',
    privacy: '/privacy',
    help: '/help',
    trademarks: '/trademarks',
  },
  jp: {
    adChoices: '/privacy#advertisements',
    advertise: 'https://www.bloombergmedia.com/contact',
    careers:
      'https://www.bloomberg.co.jp/company/what-we-do/?utm_source=jpregional&utm_medium',
    tos: '/notices/jp/tos',
    privacy: '/notices/jp/privacy',
    help: '/notices/jp/help',
    trademarks: '/notices/jp/trademarks',
  },
};

export const Footer = React.forwardRef<HTMLDivElement, FooterProps>(
  function FooterComponent(props: FooterProps, ref) {
    const translateMessage = useTranslateMessage();
    const {
      prefix = '',
      cmpText = translateMessage({
        id: 'media-ui-nav_doNotSellorSharePersonalInfo',
        defaultMessage: 'Do Not Sell or Share My Personal Information',
      }),
      name = 'footer',
      desktopBreakpointClassName,
      addPrivacyManager,
      isWeekend,
      locale = 'en',
      region = REGIONS.US,
      footerMenuItemsUrl,
      theme = THEMES.DARK,
      showLogo = true,
      footerMenuItems,
      /* c8 ignore next*/
      trackingFunction = () => {},
    } = props;

    const cmpRef = React.useRef<HTMLButtonElement>(null);
    const [_footerMenuItems, setFooterMenuItems] = useState(footerMenuItems);
    const isCMPEnabled = locale !== 'jp';

    useEffect(() => {
      addPrivacyManager(cmpRef.current!);
    }, []);

    const fetchFooterData = useCallback(async () => {
      if (!footerMenuItemsUrl) {
        return;
      }

      try {
        const response = await fetch(footerMenuItemsUrl);
        const footerMenuItemsJson = await response.json();

        if (footerMenuItemsJson && Object.keys(footerMenuItemsJson).length) {
          setFooterMenuItems(footerMenuItemsJson);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          `Unable to fetch footer items from ${footerMenuItemsUrl}: ${e}`
        );
      }
    }, [footerMenuItemsUrl]);

    useEffect(() => {
      fetchFooterData();
    }, [footerMenuItemsUrl]);

    const logoTrackingFunction = useCallback(
      (eventLabel: string) => {
        trackingFunction('click', eventLabel);
      },
      [trackingFunction]
    );

    const menuItemTrackingFunction = useCallback(
      (eventLabel: string) => {
        trackingFunction('click', eventLabel);
      },
      [trackingFunction]
    );

    const footerTrackingFunction = useCallback(
      (eventLabel: string) => {
        trackingFunction('click', eventLabel);
      },
      [trackingFunction]
    );

    const copyrightYear = new Date().getFullYear();
    const componentName = useSlug(name);

    const cx = classes(
      [
        styles.footerContainer,
        {
          [styles.light]: theme === THEMES.LIGHT,
          [styles.dark]: theme === THEMES.DARK,
          [styles.blue]: theme === THEMES.BLUE,
        },
      ],
      {
        name,
      }
    );

    const getLocaleUrl = useCallback(
      (key: string) => {
        const localeMap = LOCALE_URL_MAP[locale] ?? LOCALE_URL_MAP.en;

        return `${prefix}${localeMap?.[key] ?? LOCALE_URL_MAP.en?.[key] ?? ''}`;
      },
      [locale, prefix]
    );

    const handleOnTermsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      footerTrackingFunction('Terms of Service');
      const url = getLocaleUrl('tos');

      window.location.assign(url);
    };

    const handleOnTrademarksClick = (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.preventDefault();
      footerTrackingFunction('Trademarks');
      const url = getLocaleUrl('trademarks');

      window.location.assign(url);
    };

    const handleOnPrivacyPolicyClick = (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.preventDefault();
      footerTrackingFunction('Privacy Policy');
      const url = getLocaleUrl('privacy');

      window.location.assign(url);
    };

    const handleOnCareersClick = (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.preventDefault();
      footerTrackingFunction('Careers');
      const url = getLocaleUrl('careers');

      window.location.assign(url);
    };

    const handleOnAdvertisingClick = (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.preventDefault();
      footerTrackingFunction('Advertise');
      const url = getLocaleUrl('advertise');

      window.location.assign(url);
    };

    const handleOnHelpClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      footerTrackingFunction('Help');
      const url = getLocaleUrl('help');

      window.location.assign(url);
    };

    const handleOnAdChoicesClick = (
      event: React.MouseEvent<HTMLAnchorElement>
    ) => {
      event.preventDefault();
      footerTrackingFunction('Ad Choices');
      const url = getLocaleUrl('adChoices');

      window.location.assign(url);
    };

    return (
      <NavProvider region={region} locale={locale}>
        <div className={cx} ref={ref} data-component={componentName}>
          <div
            className={classes([styles.topContent, desktopBreakpointClassName])}
          >
            {showLogo && (
              <div className={styles.logo}>
                <Logo
                  trackingFunction={logoTrackingFunction}
                  isWeekend={isWeekend}
                  theme={theme}
                />
              </div>
            )}
            {_footerMenuItems && (
              <DropdownMenuDesktop
                trackingFunction={(
                  submenuText: string,
                  dropdownListItemText: string
                ) => {
                  menuItemTrackingFunction(
                    `footer-${submenuText}-${dropdownListItemText}`
                  );
                }}
                desktopBreakpointClassName={desktopBreakpointClassName}
                menuItemId="footer-items-menu"
                submenuItems={_footerMenuItems}
                theme={theme}
              />
            )}
          </div>
          <div
            className={classes([
              styles.bottomContent,
              desktopBreakpointClassName,
            ])}
          >
            <div className={styles.bottomLeftContent}>
              <a
                className={styles.link}
                data-testid="footer-link"
                onClick={handleOnTermsClick}
              >
                <TranslatedMessage
                  id="media-ui-nav_termsOfService"
                  defaultMessage="Terms of Service"
                />
              </a>
              {isCMPEnabled && (
                <button
                  className={styles.link}
                  id="cmp-footer-link"
                  data-testid="cmp-footer-link"
                  data-lang="en"
                  type="button"
                  ref={cmpRef}
                  onClick={() => footerTrackingFunction('CMP')}
                >
                  {cmpText}
                </button>
              )}
              <a
                className={styles.link}
                data-testid="footer-link"
                onClick={handleOnTrademarksClick}
              >
                <TranslatedMessage
                  id="media-ui-nav_trademarks"
                  defaultMessage="Trademarks"
                />
              </a>
              <a
                className={styles.link}
                data-testid="footer-link"
                onClick={handleOnPrivacyPolicyClick}
              >
                <TranslatedMessage
                  id="media-ui-nav_privacyPolicy"
                  defaultMessage="Privacy Policy"
                />
              </a>
            </div>
            <div className={styles.bottomRightContent}>
              <a
                className={styles.link}
                data-testid="footer-link"
                onClick={handleOnCareersClick}
              >
                <TranslatedMessage
                  id="media-ui-nav_careers"
                  defaultMessage="Careers"
                />
              </a>
              <a
                className={styles.link}
                data-testid="footer-link"
                onClick={handleOnAdvertisingClick}
              >
                <TranslatedMessage
                  id="media-ui-nav_advertise"
                  defaultMessage="Advertise"
                />
              </a>
              <div className={styles.link}>
                <a data-testid="footer-link" onClick={handleOnAdChoicesClick}>
                  <TranslatedMessage
                    id="media-ui-nav_adchoices"
                    defaultMessage="Ad Choices"
                  />
                </a>
                <AdChoices width={12} height={12} />
              </div>
              <a
                className={styles.link}
                data-testid="footer-link"
                onClick={handleOnHelpClick}
              >
                <TranslatedMessage
                  id="media-ui-nav_help"
                  defaultMessage="Help"
                />
              </a>
              <span
                className={styles.copyright}
                onClick={() => footerTrackingFunction('Copyright')}
              >
                <TranslatedMessage
                  id="media-ui-nav_bloombergAllRightsReserved"
                  defaultMessage="©{year} Bloomberg L.P. All Rights Reserved."
                  values={{ year: copyrightYear }}
                />
              </span>
            </div>
          </div>
        </div>
      </NavProvider>
    );
  }
);
